import { useState, type ReactElement, type ComponentPropsWithoutRef } from 'react';
import cx from 'clsx';
import { stringHash } from '../../utils/string-hash';
import styles from './avatar.module.css';

export type AvatarSize = '88' | '64' | '56' | '48' | '40' | '32' | '30' | '24' | '16';

const TOTAL_COLORS = 8;

interface ContentRendererProps {
  content?: ReactElement;
  showInitials?: boolean;
  setImageError?: (error: boolean) => void;
  name: string;
  props: ComponentPropsWithoutRef<'img'>;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map(initial => (initial[0] ? initial[0].toUpperCase() : ''))
    .slice(0, 2)
    .join('');
}

function ContentRenderer({
  content,
  name,
  setImageError,
  showInitials,
  props,
}: ContentRendererProps): ReactElement {
  if (content) {
    return <div className={cx(styles['avatar-content'], props.className)}>{content}</div>;
  }

  if (showInitials) {
    const initials = getInitials(name);

    return <span className={props.className}>{initials}</span>;
  }

  const { alt, loading, className, ...propsRest } = props;

  const onError = (): void => {
    setImageError?.(true);
  };

  return (
    <img
      alt={alt ?? ''}
      className={cx(styles.avatar, className)}
      data-test-avatar
      loading={loading ?? 'eager'}
      onError={onError}
      {...propsRest}
    />
  );
}

interface AvatarProps extends Omit<ComponentPropsWithoutRef<'img'>, 'content'> {
  size?: AvatarSize;
  name?: string;
  content?: ReactElement;
  withBorder?: boolean;
  className?: string;
  isDisabled?: boolean;
}

export function Avatar({
  size = '32',
  name = '',
  isDisabled = false,
  withBorder = false,
  content,
  ...props
}: AvatarProps): ReactElement {
  const [imageError, setImageError] = useState(Boolean(!props.src));

  const hasName = Boolean(name.trim());
  const showInitials = hasName && imageError;
  const index =
    stringHash(name) % TOTAL_COLORS > TOTAL_COLORS ? 0 : stringHash(name) % TOTAL_COLORS;

  return (
    <div
      className={cx(
        styles['avatar-wrapper'],
        styles[`size-${size}`],
        imageError && hasName && styles['avatar-initials'],
        imageError && hasName && styles[`avatar-initials-color-${index}`],
        withBorder && styles['avatar-borders'],
        isDisabled && styles['avatar-disabled']
      )}
      data-test-avatar-wrapper
    >
      <ContentRenderer
        content={content}
        name={name}
        props={props}
        setImageError={setImageError}
        showInitials={showInitials}
      />
    </div>
  );
}
